import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Layout, theme, Grid, Button, Tooltip, Badge, Space } from "antd";
import { Outlet } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useAppContext } from "../context/AppContext";
import {
  getThemeSettings,
  getStoredThemeSettings,
  saveStoredThemeSettings,
} from "../services/themeService";
import { useAuth } from "../context/AuthContext";
import { LogoutOutlined, BellOutlined, CalendarOutlined, ProjectOutlined } from "@ant-design/icons";
import { permissionService } from "../services/permissionService";
import DesignStageNavigation from "../navigation/DesignStageNavigationContainer";
import { StageMetaProvider } from "../context/StageMetaContext";
import { DesignStageNotificationProvider, useDesignStageNotifications } from "../context/DesignStageNotificationContext";
import DesignStageNotificationPanel from "../components/DesignStageNotificationPanel";
import { DesignPreferencesProvider } from "../context/DesignPreferencesContext";
import axiosInstance from "../config/axios";
import dayjs from "dayjs";

const { useBreakpoint } = Grid;

// Helper function to get days remaining and urgency status
const getDateStatus = (dateStr: string | null) => {
  if (!dateStr) return null;
  const targetDate = dayjs(dateStr);
  const today = dayjs();
  const daysRemaining = targetDate.diff(today, 'day');
  const weeksRemaining = Math.ceil(daysRemaining / 7);
  
  let status: 'urgent' | 'warning' | 'normal' | 'passed' = 'normal';
  let color = '#52c41a'; // green
  
  if (daysRemaining < 0) {
    status = 'passed';
    color = '#ff7875'; // muted red
  } else if (weeksRemaining <= 3) {
    status = 'urgent';
    color = '#fa8c16'; // orange
  } else if (weeksRemaining <= 6) {
    status = 'warning';
    color = '#1890ff'; // blue
  }
  
  return { daysRemaining, weeksRemaining, status, color };
};

const DesignLayoutInner: React.FC = () => {
  const {
    state: { isNavMenuClose },
  } = useAppContext();
  const { theme: currentTheme, updateTheme } = useTheme();
  const { user, logout } = useAuth();
  const { unreadCount } = useDesignStageNotifications();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isSmallDesktop = screens.md && !screens.lg; // 768px to 1200px
  const isDarkMode = currentTheme.algorithm?.[0] === theme.darkAlgorithm;

  const [notificationPanelVisible, setNotificationPanelVisible] = useState(false);
  const [partyName, setPartyName] = useState<string>("");
  const [timelineData, setTimelineData] = useState<{
    design: string | null;
    project: string | null;
  }>({ design: null, project: null });

  const themeTokens = useMemo(
    () => ({
      background: isDarkMode ? "#141414" : "#f5f7fa",
      contentBackground: isDarkMode ? "#1f1f1f" : "#ffffff",
      textColor: isDarkMode
        ? "rgba(255, 255, 255, 0.85)"
        : "rgba(0, 0, 0, 0.85)",
      textColorSecondary: isDarkMode
        ? "rgba(255, 255, 255, 0.45)"
        : "rgba(0, 0, 0, 0.45)",
      borderColor: isDarkMode ? "#303030" : "#f0f0f0",
      primaryColor: currentTheme.token?.colorPrimary || "#1d8348",
    }),
    [isDarkMode, currentTheme.token?.colorPrimary]
  );

  const loadThemeFromAPI = useCallback(async () => {
    try {
      if (getStoredThemeSettings()) {
        return;
      }

      const apiSettings = await getThemeSettings();
      if (apiSettings) {
        saveStoredThemeSettings(apiSettings);
        updateTheme(apiSettings);
      }
    } catch (error) {
      console.error("Failed to load theme settings in LandStageLayout:", error);
    }
  }, [updateTheme]);

  useEffect(() => {
    const storedPartyName = localStorage.getItem("selectedPartyName");
    if (storedPartyName) {
      setPartyName(storedPartyName);
    }
  }, []);

  const fetchTimelineData = useCallback(async () => {
    const partyId = localStorage.getItem("selectedPartyId");
    if (!partyId) return;

    try {
      const response = await axiosInstance.get(`/api/dashboard/party/${partyId}/timeline`);
      if (response.data?.success && response.data?.data) {
        setTimelineData({
          design: response.data.data.design,
          project: response.data.data.project,
        });
      }
    } catch (error) {
      console.debug("Failed to fetch timeline data:", error);
    }
  }, []);

  useEffect(() => {
    fetchTimelineData();
  }, [fetchTimelineData]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "selectedPartyId") {
        fetchTimelineData();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [fetchTimelineData]);

  useEffect(() => {
    const handlePartyNameChange = (event: StorageEvent) => {
      if (event.key === "selectedPartyName") {
        setPartyName(event.newValue || "");
      }
    };

    window.addEventListener("storage", handlePartyNameChange);
    return () => window.removeEventListener("storage", handlePartyNameChange);
  }, []);

  const loadStagePermissions = useCallback(async () => {
    const partyId = localStorage.getItem("selectedPartyId");
    const stageId = localStorage.getItem("selectedStageId");
    if (!partyId || !stageId || !user?.id) {
      console.debug(
        "Cannot load stage permissions: no party or stage selected"
      );
      return;
    }

    try {
      await permissionService.refreshStagePermissions(
        stageId,
        user.id || "",
        partyId
      );
    } catch (error) {
      console.error("Failed to load stage permissions in LandStageLayout:", error);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!getStoredThemeSettings()) {
      loadThemeFromAPI();
    }
  }, [loadThemeFromAPI]);

  useEffect(() => {
    loadStagePermissions();
  }, [loadStagePermissions]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "selectedPartyId" || event.key === "selectedStageId") {
        loadStagePermissions();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [loadStagePermissions]);

  const layoutStyles = useMemo(
    () => ({
      backgroundColor: themeTokens.background,
      minHeight: "100vh",
      height: "100%",
      overflow: "hidden",
      display: "flex",
      color: themeTokens.textColor,
      transition: "all 0.3s ease-in-out",
    }),
    [themeTokens]
  );

  const contentStyles = useMemo(
    () => ({
      marginLeft: isMobile
        ? 0
        : isNavMenuClose
        ? 70
        : isSmallDesktop
        ? 180
        : 220,
      width: "100%",
      padding: isMobile ? "56px 16px 16px" : "0",
      overflow: "auto",
      minHeight: "100vh",
      background: themeTokens.contentBackground,
      transition: "all 0.3s ease-in-out",
      color: themeTokens.textColor,
    }),
    [themeTokens, isNavMenuClose, isMobile, isSmallDesktop]
  );

  const headerStyles = useMemo(
    () => ({
      background: "transparent",
      padding: isMobile ? "8px 12px" : "8px 16px",
      height: 52,
      lineHeight: "52px",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end" as const,
      color: themeTokens.textColor,
      backdropFilter: "none",
    }),
    [isMobile, themeTokens.textColor]
  );

  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      isDarkMode ? "dark" : "light"
    );
    document.body.style.backgroundColor = themeTokens.background;
    document.body.style.color = themeTokens.textColor;

    const style = document.createElement("style");
    style.textContent = `
      :root {
        color-scheme: ${isDarkMode ? "dark" : "light"};
        --primary-color: ${themeTokens.primaryColor};
        --background-color: ${themeTokens.background};
        --content-background: ${themeTokens.contentBackground};
        --text-color: ${themeTokens.textColor};
        --text-color-secondary: ${themeTokens.textColorSecondary};
        --border-color: ${themeTokens.borderColor};
      }

      /* Ensure solid backgrounds for common surfaces/popups */
      .ant-card,
      .ant-modal .ant-modal-content,
      .ant-popover .ant-popover-inner,
      .ant-dropdown .ant-dropdown-menu,
      .ant-select-dropdown {
        background: var(--content-background) !important;
      }

      /* Optional: content containers that may rely on transparent parents */
      .ant-card-bordered,
      .ant-modal-content,
      .ant-popover-inner {
        border-color: var(--border-color) !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [isDarkMode, themeTokens]);

  // Compute date statuses
  const designStatus = getDateStatus(timelineData.design);
  const projectStatus = getDateStatus(timelineData.project);

  return (
    <Layout style={layoutStyles}>
     <DesignStageNavigation />
      <Layout style={contentStyles}>
        <Layout.Header style={headerStyles}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "space-between",
            width: "100%"
          }}>
            {/* Left side - Party Name */}
            <div style={{ 
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              flex: 1
            }}>
              <div style={{ 
                fontWeight: 600, 
                fontSize: isMobile ? "16px" : "23px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}>
                {partyName || ""}
              </div>
            </div>
            
            {/* Right side - Timeline Dates, Notifications, Logout, and Username */}
            <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 16 }}>
              {/* Timeline Dates */}
              {!isMobile && (timelineData.design || timelineData.project) && (
                <Space size={16} style={{ marginRight: 8 }}>
                  {timelineData.design && designStatus && (
                    <Tooltip title={
                      designStatus.daysRemaining < 0 
                        ? `Design deadline passed ${Math.abs(designStatus.daysRemaining)} days ago` 
                        : `${designStatus.daysRemaining} days remaining`
                    }>
                      <div style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 6,
                        padding: "4px 12px",
                        borderRadius: 6,
                        background: isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)"
                      }}>
                        <CalendarOutlined style={{ color: designStatus.color, fontSize: 16 }} />
                        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                          <span style={{ 
                            fontSize: 11, 
                            color: themeTokens.textColorSecondary,
                            fontWeight: 500,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px"
                          }}>
                            Design Due
                          </span>
                          <span style={{ 
                            fontSize: 15, 
                            fontWeight: 600, 
                            color: designStatus.color 
                          }}>
                            {dayjs(timelineData.design).format("DD MMM YYYY")}
                          </span>
                        </div>
                      </div>
                    </Tooltip>
                  )}
                  {timelineData.project && projectStatus && (
                    <Tooltip title={
                      projectStatus.daysRemaining < 0 
                        ? `Project deadline passed ${Math.abs(projectStatus.daysRemaining)} days ago` 
                        : `${projectStatus.daysRemaining} days remaining`
                    }>
                      <div style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 6,
                        padding: "4px 12px",
                        borderRadius: 6,
                        background: isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)"
                      }}>
                        <ProjectOutlined style={{ color: projectStatus.color, fontSize: 16 }} />
                        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                          <span style={{ 
                            fontSize: 11, 
                            color: themeTokens.textColorSecondary,
                            fontWeight: 500,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px"
                          }}>
                            Project Due
                          </span>
                          <span style={{ 
                            fontSize: 15, 
                            fontWeight: 600, 
                            color: projectStatus.color 
                          }}>
                            {dayjs(timelineData.project).format("DD MMM YYYY")}
                          </span>
                        </div>
                      </div>
                    </Tooltip>
                  )}
                </Space>
              )}

              <Tooltip title="Notifications">
                <Badge count={unreadCount} size="small" offset={[-2, 2]}>
                  <Button
                    type="text"
                    shape="circle"
                    size="small"
                    icon={<BellOutlined />}
                    onClick={() => setNotificationPanelVisible(true)}
                  />
                </Badge>
              </Tooltip>
              <Tooltip title="Logout">
                <Button
                  type="text"
                  shape="circle"
                  size="small"
                  icon={<LogoutOutlined />}
                  onClick={logout}
                />
              </Tooltip>
              <Tooltip title={user?.email || ""}>
                <div style={{ 
                  fontWeight: 600, 
                  cursor: "default",
                  fontSize: isMobile ? "14px" : "23px"
                }}>
                  {user?.name || "User"}
                </div>
              </Tooltip>
            </div>
          </div>
        </Layout.Header>
        <Layout.Content>
          <Outlet />
        </Layout.Content>
      </Layout>
      <DesignStageNotificationPanel
        visible={notificationPanelVisible}
        onClose={() => setNotificationPanelVisible(false)}
      />
    </Layout>
  );
};

const DesignLayout: React.FC = () => {
  return (
    <StageMetaProvider>
      <DesignPreferencesProvider>
        <DesignStageNotificationProvider>
          <DesignLayoutInner />
        </DesignStageNotificationProvider>
      </DesignPreferencesProvider>
    </StageMetaProvider>
  );
};

export default React.memo(DesignLayout);
